import { useMemo } from 'react';

import { supabaseClient, useUser } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
    upsertSwingThread,
    getSwingThreads,
    SwingThreadPayload,
    softDeleteSwingThread,
    getSwingThreadIdsByEntity,
    getSwingThreadByStudentIds,
} from '@data';

import { queryClient } from '../config';

export const getSwingThreadsKey = (swingUuid?: string) => ['swing', swingUuid, 'threads'] as const;
export const getSwingThreadsByEntityKey = (activityId?: number, swingUuid?: string) =>
    ['swing-threads', 'activity', activityId ?? null, 'swing', swingUuid ?? null, 'count'] as const;
export const getSwingThreadByStudentIdsKey = (studentIds: string[]) =>
    ['thread-presence', ...[...studentIds].sort()] as const;

export const useSwingThreadsByStudentIds = (studentIds: string[]) => {
    const { data: user } = useUser();

    const sortedStudentIds = useMemo(() => [...studentIds].sort(), [studentIds]);

    return useQuery({
        queryKey: getSwingThreadByStudentIdsKey(sortedStudentIds),
        queryFn: () => getSwingThreadByStudentIds(supabaseClient, sortedStudentIds),
        enabled: Boolean(user?.id) && sortedStudentIds.length > 0,
        refetchOnMount: false,
    });
};

export const useSwingThreadsByEntity = (activityId?: number, swingUuid?: string) => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: getSwingThreadsByEntityKey(activityId, swingUuid),
        queryFn: () => {
            return getSwingThreadIdsByEntity(supabaseClient, activityId, swingUuid);
        },
        enabled: Boolean(user?.id),
        refetchOnMount: false,
    });
};

export const useSwingThreads = (swingUuid?: string) => {
    return useQuery({
        queryKey: getSwingThreadsKey(swingUuid),
        queryFn: () => {
            if (!swingUuid) {
                return [];
            }
            return getSwingThreads(supabaseClient, swingUuid);
        },
        enabled: Boolean(swingUuid),
        refetchOnMount: false,
    });
};

export const useUpsertSwingThread = () => {
    const { data: user } = useUser();

    return useMutation({
        mutationFn: (payload: Omit<SwingThreadPayload, 'createdBy'>) =>
            upsertSwingThread(supabaseClient, { ...payload, createdBy: user!.id }),
        onSuccess: (_, { swingUuid }) => {
            queryClient.invalidateQueries({ queryKey: getSwingThreadsKey(swingUuid) });
        },
    });
};

export const useDeleteSwingThread = () => {
    return useMutation({
        mutationFn: (payload: Pick<SwingThreadPayload, 'id' | 'swingUuid'>) =>
            softDeleteSwingThread(supabaseClient, payload),
        onSuccess: (_, variables) => {
            if ('swingUuid' in variables && variables.swingUuid) {
                queryClient.invalidateQueries({
                    queryKey: getSwingThreadsKey(variables.swingUuid),
                });
            }
        },
    });
};
